import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Home, 
  MapPin, 
  Maximize, 
  IndianRupee, 
  BedDouble,
  Phone,
  Mail,
  CheckCircle,
  Building
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import api from "../lib/api";
import { toast } from 'sonner';
import { businessInfo } from '../mock';


const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchProperty();
  }, [id]);

  useEffect(() => {
    if (property) {
      document.title = `${property.name} - ${property.location} | CHIV Properties Greater Noida`;
    }
  }, [property]);

  const fetchProperty = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Failed to load property details');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { color: 'bg-green-100 text-green-800', label: 'Available' },
      sold: { color: 'bg-red-100 text-red-800', label: 'Sold' },
      under_construction: { color: 'bg-yellow-100 text-yellow-800', label: 'Under Construction' }
    };
    return statusConfig[status] || statusConfig.available;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  if (!property) return null;

  const statusConfig = getStatusBadge(property.status);
  const images = property.images.length > 0 ? property.images : ['https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800'];

  const cleanPhone = (p) => p ? p.replace(/\D/g, '') : '';
  const contactPhone = property.ownerPhone || businessInfo.phone;
  const whatsappNumber = property.ownerPhone 
    ? (cleanPhone(property.ownerPhone).startsWith('91') ? cleanPhone(property.ownerPhone) : `91${cleanPhone(property.ownerPhone)}`)
    : businessInfo.whatsappNumber;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/properties')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center space-x-2">
              <Home className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{businessInfo.name}</h1>
                <p className="text-xs text-gray-600 italic">{businessInfo.tagline}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <Card className="overflow-hidden">
              <div className="relative h-[400px] md:h-[500px]">
                <img
                  src={images[selectedImage]}
                  alt={property.name}
                  className="w-full h-full object-cover"
                />
                {property.featured && (
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-amber-500 text-white">Featured Property</Badge>
                  </div>
                )}
                <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
                  <Badge className={property.listingType === 'rent' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'}>
                    {property.listingType === 'rent' ? 'For Rent' : 'For Sale'}
                  </Badge>
                  <Badge className={statusConfig.color}>
                    {statusConfig.label}
                  </Badge>
                </div>
              </div>
              {images.length > 1 && (
                <div className="p-4 grid grid-cols-4 md:grid-cols-6 gap-2">
                  {images.map((img, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index ? 'border-blue-600' : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${property.name} - ${index + 1}`}
                        className="w-full h-20 object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Property Details */}
            <Card>
              <CardContent className="p-6 space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{property.name}</h1>
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-5 w-5 mr-2 text-blue-600" />
                    <span className="text-lg">{property.location}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-4 text-sm">
                  <Badge variant="outline" className="text-blue-600 border-blue-600">
                    <Building className="h-4 w-4 mr-1" />
                    {property.propertyType}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 py-6 border-t border-b">
                  {property.bhk && (
                    <div>
                      <div className="flex items-center text-gray-500 mb-1">
                        <BedDouble className="h-5 w-5 mr-2 text-blue-600" />
                        <span className="text-sm">Configuration</span>
                      </div>
                      <p className="text-xl font-bold text-gray-900">{property.bhk}</p>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center text-gray-500 mb-1">
                      <Maximize className="h-5 w-5 mr-2 text-blue-600" />
                      <span className="text-sm">Area</span>
                    </div>
                    <p className="text-xl font-bold text-gray-900">{property.area}</p>
                  </div>
                  <div>
                    <div className="flex items-center text-gray-500 mb-1">
                      <IndianRupee className="h-5 w-5 mr-2 text-blue-600" />
                      <span className="text-sm">{property.listingType === 'rent' ? 'Monthly Rent' : 'Price'}</span>
                    </div>
                    <p className="text-xl font-bold text-blue-600">
                      {property.price}
                      {property.listingType === 'rent' && (
                        <span className="text-sm text-gray-500 font-normal ml-1">/month</span>
                      )}
                    </p>
                  </div>
                </div>

                {property.description && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Description</h2>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {property.description}
                    </p>
                  </div>
                )}

                {property.highlights && property.highlights.length > 0 && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Highlights</h2>
                    <ul className="grid md:grid-cols-2 gap-3">
                      {property.highlights.map((highlight, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {property.amenities && property.amenities.length > 0 && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Amenities</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {property.amenities.map((amenity, index) => (
                        <div key={index} className="flex items-center bg-blue-50 rounded-lg p-3">
                          <CheckCircle className="h-5 w-5 text-blue-600 mr-2" />
                          <span className="text-gray-800 text-sm">{amenity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Contact Card */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="p-6 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {property.ownerPhone ? 'Contact Owner' : 'Interested in this property?'}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {property.ownerPhone ? 'Contact the owner directly for more details' : 'Contact us for more details or schedule a site visit'}
                  </p>
                </div>

                <div className="space-y-3">
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700" 
                    size="lg"
                    onClick={() => window.location.href = `tel:${contactPhone}`}
                  >
                    <Phone className="h-5 w-5 mr-2" />
                    Call Now
                  </Button>
                  
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700" 
                    size="lg"
                    onClick={() => {
                      const message = `Hi, I'm interested in "${property.name}" located at ${property.location}. Please share more details.`;
                      window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
                    }}
                  >
                    WhatsApp Us
                  </Button>

                  <Button 
                    variant="outline" 
                    className="w-full" 
                    size="lg"
                    onClick={() => navigate('/#contact')}
                  >
                    <Mail className="h-5 w-5 mr-2" />
                    Submit Inquiry
                  </Button>
                </div>

                <div className="pt-6 border-t space-y-3">
                  {property.ownerPhone ? (
                    <>
                      <div className="flex items-center text-gray-700">
                        <Phone className="h-5 w-5 text-blue-600 mr-3 animate-pulse" />
                        <div>
                          <p className="text-xs text-gray-500">Call Owner ({property.ownerName || 'Direct'})</p>
                          <p className="font-semibold">{property.ownerPhone}</p>
                        </div>
                      </div>
                      {property.ownerEmail && (
                        <div className="flex items-center text-gray-700">
                          <Mail className="h-5 w-5 text-blue-600 mr-3" />
                          <div>
                            <p className="text-xs text-gray-500">Email Owner</p>
                            <p className="font-semibold text-sm truncate max-w-[180px]">{property.ownerEmail}</p>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center text-gray-700">
                        <Phone className="h-5 w-5 text-blue-600 mr-3" />
                        <div>
                          <p className="text-xs text-gray-500">Call us at</p>
                          <p className="font-semibold">{businessInfo.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Mail className="h-5 w-5 text-blue-600 mr-3" />
                        <div>
                          <p className="text-xs text-gray-500">Email us at</p>
                          <p className="font-semibold text-sm">{businessInfo.email}</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
